import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Lets a page with unsaved work ask before something OUTSIDE the page moves
 * the user on.
 *
 * The site runs on a BrowserRouter, which has no route blocker, so each page
 * guards its own ways out (Resident Detail's Back to list and breadcrumb ask
 * Discard / Save & Leave / Cancel). The top bar's search is not one of the
 * page's own ways out: picking another resident there changed the URL under
 * an edited resident page without asking. A page registers a guard here; the
 * search calls `requestNavigation` instead of navigating directly.
 *
 * A guard returns true when it has taken over (it shows its own prompt and
 * navigates itself if the user says so), or false to let the navigation go.
 */
export type LeaveGuard = (path: string) => boolean;

let guards: LeaveGuard[] = [];

/** Registers `guard` while the calling component is mounted. Reads the latest guard each time. */
export function useLeaveGuard(guard: LeaveGuard): void {
  const ref = useRef(guard);
  useEffect(() => { ref.current = guard; }, [guard]);
  useEffect(() => {
    const registered: LeaveGuard = (path) => ref.current(path);
    guards.push(registered);
    return () => { guards = guards.filter((g) => g !== registered); };
  }, []);
}

/**
 * Asks the innermost registered guard about leaving for `path`.
 * Returns true when a guard took over, false when the caller should navigate.
 */
export function leaveGuardTakesOver(path: string): boolean {
  for (let i = guards.length - 1; i >= 0; i--) {
    if (guards[i](path)) return true;
  }
  return false;
}

/** A page marks unsaved work with data-dirty="true" on its root (Item Add/Mod does). */
export function pageHasUnsavedMarker(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[data-dirty="true"]') != null;
}

/** Test seam. */
export function resetLeaveGuards(): void {
  guards = [];
}

/**
 * `navigate`, but it asks the registered guards first.
 *
 * The sidebar and the hub cards call plain `navigate`, which walked
 * straight past a page's unsaved work — the guard registered here was only
 * ever consulted by the top bar's search. Any nav surface that routes
 * through this gives every guarded page the same prompt.
 */
export function useGuardedNavigate() {
  const navigate = useNavigate();
  return useCallback((to: string, options?: { replace?: boolean }) => {
    if (leaveGuardTakesOver(to)) return;
    // Forwarded with the SAME arity the caller used — passing an explicit
    // `undefined` second argument changes the call shape for anything
    // observing it.
    if (options) navigate(to, options);
    else navigate(to);
  }, [navigate]);
}
